<template>
  <div class="min-h-screen bg-linear-to-br from-brand-blue-50 via-white to-brand-blue-100 flex items-center justify-center px-4 py-8">
    <div class="max-w-2xl w-full">
      <!-- Animated Error Container -->
      <div class="text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <!-- Error Illustration -->
        <div class="relative h-48 md:h-64 flex items-center justify-center mb-8">
          <!-- Animated background elements -->
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="animate-pulse">
              <div
                class="inline-flex items-center justify-center w-32 h-32 md:w-40 md:h-40 rounded-full bg-linear-to-br from-brand-blue-200 to-brand-blue-100"
              >
                <span class="text-8xl md:text-9xl select-none">
                  {{ errorIcon }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Code -->
        <div>
          <h1 class="text-6xl md:text-7xl font-black bg-clip-text text-transparent bg-linear-to-r from-brand-blue-600 to-brand-red-600 mb-2">
            {{ statusCode }}
          </h1>
          <p class="text-xl md:text-2xl font-bold text-gray-800">
            {{ errorTitle }}
          </p>
        </div>

        <!-- Error Message -->
        <div class="bg-white rounded-lg shadow-md p-6 border-l-4 border-brand-blue-600">
          <p class="text-gray-600 text-lg leading-relaxed">
            {{ errorMessage }}
          </p>
        </div>

        <!-- Fun Quote -->
        <div class="bg-brand-blue-50 rounded-lg p-4 md:p-6 italic text-gray-700">
          "{{ funQuote }}"
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <UButton
            to="/"
            size="lg"
            color="primary"
            variant="solid"
            class="font-semibold"
            trailing-icon="i-heroicons-arrow-left-20-solid"
          >
            Retour à l'accueil
          </UButton>
          <UButton
            to="/contact"
            size="lg"
            color="secondary"
            variant="outline"
            class="font-semibold"
            trailing-icon="i-heroicons-envelope-20-solid"
          >
            Nous contacter
          </UButton>
        </div>

        <!-- Helpful Tips -->
        <div class="mt-12 pt-8 border-t border-gray-200">
          <p class="text-sm text-gray-500 mb-4">
            💡 Vous pouvez aussi essayer :
          </p>
          <div class="flex flex-wrap gap-2 justify-center">
            <UButton
              to="/prestations"
              size="sm"
              variant="ghost"
              color="secondary"
            >
              Nos prestations
            </UButton>
            <UButton
              to="/formations"
              size="sm"
              variant="ghost"
              color="secondary"
            >
              Nos formations
            </UButton>
            <UButton
              to="/qui-sommes-nous"
              size="sm"
              variant="ghost"
              color="secondary"
            >
              Qui sommes-nous
            </UButton>
          </div>
        </div>
      </div>

      <!-- Floating animation elements -->
      <div class="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          class="absolute top-10 left-10 text-4xl opacity-10 animate-bounce"
          style="animation-delay: 0s"
        >
          🚑
        </div>
        <div
          class="absolute bottom-20 right-10 text-4xl opacity-10 animate-bounce"
          style="animation-delay: 1s"
        >
          ⚕️
        </div>
        <div class="absolute top-1/3 right-10 text-3xl opacity-5 animate-pulse">
          ❤️
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// Get error details
const error = useError()
const statusCode = computed(() => error.value?.statusCode || 404)

// Error configurations by status code
const errorConfigs: Record<number, {
  title: string
  pageTitle: string
  message: string
  icon: string
  quotes: string[]
}> = {
  404: {
    title: 'Page introuvable 🔍',
    pageTitle: '404 | Page introuvable | Ambulance Present',
    message: 'La page que vous cherchez semble s\'être échappée. Peut-être qu\'elle a décidé de prendre une pause méritée ! Retrouvons-la ensemble en retournant à l\'accueil.',
    icon: '🗺️',
    quotes: [
      'On s\'est perdu dans les carrefours du web, mais pas de panique !',
      'Cette page joue à cache-cache... et elle gagne !',
      'Même nos ambulanciers auraient du mal à trouver cette page !',
      'La route n\'existe pas, mais d\'autres options s\'offrent à vous.',
    ],
  },
  500: {
    title: 'Erreur serveur 💥',
    pageTitle: '500 | Erreur serveur | Ambulance Present',
    message: 'Outch, c\'est embarrassant ! Notre serveur a eu un petit problème. Nos équipes techniques sont déjà sur le coup. Merci de votre patience !',
    icon: '⚙️',
    quotes: [
      'Même nos meilleurs secouristes ne peuvent pas aider un serveur qui se sent mal.',
      'Notre infrastructure prend un repos bien mérité.',
      'Un café pour le serveur et ça repart !',
      'Les bits et les bytes dansent la mamba noire en ce moment...',
    ],
  },
  503: {
    title: 'Service indisponible 🛠️',
    pageTitle: '503 | Service indisponible | Ambulance Present',
    message: 'Le site est actuellement en maintenance. Nous travaillons pour vous offrir la meilleure expérience possible. À bientôt !',
    icon: '🔧',
    quotes: [
      'Même les ambulances ont besoin d\'une révision parfois !',
      'Notre infrastructure se refait une beauté.',
      'Soyez patients, nous serons bientôt de retour.',
      'Changement d\'huile en cours... patience !',
    ],
  },
  400: {
    title: 'Mauvaise requête ❌',
    pageTitle: '400 | Mauvaise requête | Ambulance Present',
    message: 'Votre requête ne semble pas correcte. Vérifiez l\'adresse ou réessayez avec les bonnes informations.',
    icon: '🤔',
    quotes: [
      'Cette requête sent mauvais... même pour nous.',
      'On n\'a pas compris le message !',
      'Quelque chose ne colle pas dans votre demande.',
      'Le diagnostic n\'est pas bon pour cette requête.',
    ],
  },
  401: {
    title: 'Non autorisé 🔐',
    pageTitle: '401 | Non autorisé | Ambulance Present',
    message: 'Vous devez être authentifié pour accéder à cette ressource. Veuillez vous connecter.',
    icon: '🔒',
    quotes: [
      'Comment accéder aux urgences sans être inscrit ?',
      'L\'accès refusé, mais pas votre potentiel !',
      'Identifiez-vous pour continuer.',
      'Bienvenue en zone sécurisée, s\'il vous plaît !',
    ],
  },
  403: {
    title: 'Accès refusé 🚫',
    pageTitle: '403 | Accès refusé | Ambulance Present',
    message: 'Vous n\'avez pas les permissions pour accéder à cette ressource. Contactez notre équipe si vous pensez que c\'est une erreur.',
    icon: '⛔',
    quotes: [
      'Cette zone est réservée... aux autres !',
      'L\'accès interdit est un refus poli.',
      'Vous n\'êtes pas autorisé à entrer ici.',
      'Même Sherlock ne pourrait pas accéder à cette page.',
    ],
  },
}

const config = computed(() => errorConfigs[statusCode.value] || errorConfigs[404])

const errorIcon = computed(() => config.value?.icon ?? '🚑')
const errorTitle = computed(() => config.value?.title ?? 'Oops!')
const pageTitle = computed(() => config.value?.pageTitle ?? 'Erreur | Ambulance Present')
const { notifyError } = useBugsnagReporter()

const funQuote = computed(() => {
  const quotes = config.value?.quotes ?? []
  return quotes.length > 0 ? quotes[Math.floor(Math.random() * quotes.length)] : 'Quelque chose s\'est passé.'
})

const errorMessage = computed(() => config.value?.message ?? 'Une erreur est survenue.')

onMounted(() => {
  if (error.value) {
    notifyError(error.value, {
      hook: 'error.vue',
      statusCode: statusCode.value,
    })
  }
})

useHead(() => ({
  title: pageTitle.value,
}))
</script>

<style scoped>
/* Smooth animations */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>
